import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Typography,
  Switch,
  Empty,
  Badge,
  Tag,
  Select,
  Tooltip,
  Avatar,
  Alert
} from "antd";
import {
  SendOutlined,
  NotificationOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  ClearOutlined,
  HistoryOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { postRequest, getRequest } from "../../../Utils/AppApi";
import { SEND_NOTIFICATION, LIST_ACTIVE_USERS } from "../../../Utils/appConstants";

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const NotificationPage = ({ history }) => {
  const [usersList, setUsersList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendToAll, setSendToAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);

  useEffect(() => {
    getRequest(LIST_ACTIVE_USERS, history).then(res => {
      if (res?.success) {
        setUsersList(res.data || []);
      }
    });
  }, [history]);

  const handleSend = async () => {
    if (!title || !message) {
      return toast.error("Please enter both Title and Message");
    }

    if (!sendToAll && selectedUsers.length === 0) {
      return toast.error("Please select at least one user");
    }

    setLoading(true);

    const payload = sendToAll
      ? { sendToAll: true, title, message }
      : { usernames: selectedUsers, title, message };

    try {
      const res = await postRequest(SEND_NOTIFICATION, payload, history);

      if (res?.success) {
        toast.success("Notification sent successfully");

        setHistoryList(prev => [
          {
            id: Date.now(),
            username: sendToAll
              ? "Everyone"
              : selectedUsers.join(", "),
            title,
            message,
            timestamp: new Date().toLocaleString()
          },
          ...prev
        ]);

        setSelectedUsers([]);
        setTitle("");
        setMessage("");
      } else {
        toast.error(res?.message || "Failed to send notification");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setSelectedUsers([]);
    setTitle("");
    setMessage("");
    setSendToAll(false);
  };

  const columns = [
    {
      name: "Recipient",
      selector: row => row.username,
      cell: row => (
        <Tooltip title={row.username}>
          <span style={{ fontWeight: 500 }}>{row.username}</span>
        </Tooltip>
      )
    },
    {
      name: "Title",
      selector: row => row.title,
      cell: row => <Text strong>{row.title}</Text>
    },
    {
      name: "Message",
      selector: row => row.message,
      cell: row => (
        <Tooltip title={row.message}>
          <span
            style={{
              maxWidth: 200,
              display: "inline-block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {row.message}
          </span>
        </Tooltip>
      )
    },
    {
      name: "Status",
      center: true,
      cell: () => (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Sent
        </Tag>
      )
    },
    {
      name: "Time",
      selector: row => row.timestamp,
      sortable: true
    }
  ];

  return (
   <div
      style={{
        background: "#f8fafc",
        minHeight: "100vh",
        padding: 24
      }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <Row gutter={[32, 32]}>
          {/* LEFT PANEL */}
          <Col xs={24} lg={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 20,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                backdropFilter: "blur(10px)",
                background: "rgba(255,255,255,0.95)"
              }}
              title={
                <Space size="middle">
                  <Avatar
                    icon={<NotificationOutlined />}
                    style={{
                      background: "linear-gradient(135deg,#667eea,#764ba2)",
                      color: "#fff"
                    }}
                  />
                  <Text strong style={{ fontSize: 18 }}>
                    Send Notification
                  </Text>
                </Space>
              }
              extra={
                <Tooltip title="Clear form">
                  <Button
                    type="text"
                    icon={<ClearOutlined />}
                    onClick={clearForm}
                  />
                </Tooltip>
              }
            >
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* Send to All Toggle */}
                <Card
                  size="small"
                  style={{
                    background: sendToAll ? "#e6f7ff" : "#fafafa",
                    borderRadius: 16,
                    border: sendToAll ? "1px solid #91d5ff" : "1px solid #f0f0f0",
                    transition: "all 0.3s"
                  }}
                >
                  <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                    <Space>
                      <TeamOutlined style={{ color: sendToAll ? "#1890ff" : "#8c8c8c" }} />
                      <Text strong>Send to Everyone</Text>
                    </Space>
                    <Switch
                      checked={sendToAll}
                      onChange={setSendToAll}
                      checkedChildren="All"
                      unCheckedChildren="Selected"
                    />
                  </Space>
                </Card>

                {/* User Selector */}
                {!sendToAll && (
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Select Users <span style={{ color: "#ff4d4f" }}>*</span>
                    </Text>
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Search and select users"
                      value={selectedUsers}
                      onChange={setSelectedUsers}
                      style={{ width: "100%" }}
                      optionLabelProp="label"
                      dropdownStyle={{ width: 350 }}
                      listHeight={256}
                      virtual={false}    // ← DISABLES VIRTUAL SCROLLING
                      showSearch
                      filterOption={(input, option) =>
                        option?.name?.toLowerCase().includes(input.toLowerCase()) ||
                        option?.username?.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {usersList.map(user => (
                        <Option
                          key={user.username}
                          value={user.username}
                          label={`${user.name} (${user.username})`}
                          name={user.name}
                          username={user.username}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                              paddingRight: 28,
                              boxSizing: 'border-box',
                            }}
                          >
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '200px',
                              }}
                              title={user.name}
                            >
                              {user.name}
                            </span>
                            <span
                              style={{
                                color: '#888',
                                fontSize: '0.85em',
                                marginLeft: 8,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user.username}
                            </span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                      {selectedUsers.length} user(s) selected
                    </Text>
                  </div>
                )}

                {/* Notification Title */}
                <div>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Title <span style={{ color: "#ff4d4f" }}>*</span>
                  </Text>
                  <Input
                    placeholder="e.g., System Maintenance"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    style={{ height: 48, borderRadius: 16 }}
                  />
                </div>

                {/* Notification Message */}
                <div>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Message <span style={{ color: "#ff4d4f" }}>*</span>
                  </Text>
                  <TextArea
                    rows={4}
                    placeholder="Enter your notification message here..."
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    style={{ borderRadius: 16 }}
                  />
                </div>

                {/* Live Preview */}
                {(title || message) && (
                  <Card
                    size="small"
                    style={{
                      background: "#f9f9fc",
                      borderRadius: 16,
                      border: "1px dashed #d9d9d9"
                    }}
                    title={
                      <Space>
                        <EyeOutlined />
                        <Text type="secondary">Preview</Text>
                      </Space>
                    }
                  >
                    <Alert
                      message={title || "Notification Title"}
                      description={message || "Your message will appear here."}
                      type="info"
                      showIcon
                      icon={<NotificationOutlined />}
                    />
                  </Card>
                )}

                {/* Send Button */}
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  size="large"
                  loading={loading}
                  onClick={handleSend}
                  block
                  style={{
                    height: 52,
                    borderRadius: 16,
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    border: "none",
                    boxShadow: "0 8px 16px rgba(102,126,234,0.3)",
                    transition: "transform 0.2s"
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.02)")}
                  onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
                >
                  Send Notification
                </Button>
              </Space>
            </Card>
          </Col>

          {/* RIGHT PANEL - HISTORY */}
          <Col xs={24} lg={16}>
            <Card
              bordered={false}
              style={{
                borderRadius: 20,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                backdropFilter: "blur(10px)",
                background: "rgba(255,255,255,0.95)",
                minHeight: 600
              }}
              title={
                <Space size="middle">
                  <Avatar
                    icon={<HistoryOutlined />}
                    style={{
                      background: "linear-gradient(135deg,#667eea,#764ba2)",
                      color: "#fff"
                    }}
                  />
                  <Text strong style={{ fontSize: 18 }}>
                    Notification History
                  </Text>
                </Space>
              }
              extra={
                <Badge
                  count={historyList.length}
                  style={{ backgroundColor: "#52c41a" }}
                  showZero
                />
              }
            >
              <DataTable
                columns={columns}
                data={historyList}
                pagination
                highlightOnHover
                pointerOnHover
                noDataComponent={
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No notifications sent yet"
                  >
                    <Button type="primary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                      Send your first notification
                    </Button>
                  </Empty>
                }
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: "#fafafa",
                      borderRadius: 12,
                      fontWeight: 600
                    }
                  },
                  rows: {
                    style: {
                      minHeight: "56px",
                      "&:hover": {
                        backgroundColor: "#f0f5ff",
                        transition: "background 0.2s"
                      }
                    }
                  }
                }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default NotificationPage;